import {
  BarChart,
  Bar,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  TooltipProps,
  LabelList,
} from "recharts";
import { CHART_COLORS } from "./chartTokens";
import { formatDecimal } from "@/utils";

type TimeHistogramData = {
  timeRange: string;
  leads: string | number;
  percentageOfTotal: string | number;
};

type LeadTimeHistogramCardProps = {
  title?: string;
  subtitle?: string;
  colSpan?: number;
  data: TimeHistogramData[];
};

// Custom tooltip component
const CustomTooltip = ({ active, payload }: TooltipProps<number, string>) => {
  if (active && payload && payload.length) {
    const percentage = payload[0].value;
    const leads = payload[0].payload.leads;
    const timeRange = payload[0].payload.timeRange;

    return (
      <div className="rounded-md border border-border bg-card p-3 shadow-md">
        <p className="text-sm font-semibold text-palomma-primary">{timeRange}</p>
        <p className="text-sm font-medium text-palomma-secondary">
          {leads} contactos ({typeof percentage === "number" ? formatDecimal(percentage, 1) : percentage}%)
        </p>
      </div>
    );
  }
  return null;
};

// Custom label formatter for bar values
const renderCustomizedLabel = (props: any) => {
  const { x, y, width, value } = props;
  const formattedValue = typeof value === "number" ? formatDecimal(value, 1) : value;

  return (
    <text
      x={x + width / 2}
      y={y - 10}
      fill={CHART_COLORS.axis}
      textAnchor="middle"
      dominantBaseline="middle"
      fontSize="12"
      fontWeight="500"
    >
      {formattedValue}%
    </text>
  );
};

const WINDOW_HOURS = 4;

const aggregateIntoWindows = (data: TimeHistogramData[]): TimeHistogramData[] => {
  const buckets = new Map<string, { leads: number; percentage: number }>();

  for (const item of data) {
    const startHour = parseInt(item.timeRange.split(":")[0]);
    if (Number.isNaN(startHour)) continue;

    const windowStart = Math.floor(startHour / WINDOW_HOURS) * WINDOW_HOURS;
    const windowEnd = windowStart + WINDOW_HOURS;
    const key = `${String(windowStart).padStart(2, "0")}:00-${String(windowEnd).padStart(2, "0")}:00`;

    const bucket = buckets.get(key) ?? { leads: 0, percentage: 0 };
    bucket.leads += Number(item.leads) || 0;
    bucket.percentage += Number(item.percentageOfTotal) || 0;
    buckets.set(key, bucket);
  }

  return Array.from(buckets.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([timeRange, { leads, percentage }]) => ({
      timeRange,
      leads,
      percentageOfTotal: percentage,
    }));
};

const LeadTimeHistogramCard = ({
  title = "Distribución de contactos por hora",
  subtitle = "Porcentaje de contactos creados en cada franja horaria",
  colSpan = 1,
  data,
}: LeadTimeHistogramCardProps) => {
  const aggregatedData = aggregateIntoWindows(data);
  const maxPercentage = aggregatedData.reduce(
    (max, item) => Math.max(max, Number(item.percentageOfTotal) || 0),
    0
  );

  return (
    <div style={{ gridColumn: `span ${colSpan}` }}>
      <div className="h-full rounded-2xl border border-border bg-card p-6">
        <div className="mb-4">
          <h3 className="text-sm font-semibold text-palomma-secondary">{title}</h3>
          {subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
        </div>

        <div className="h-[260px] md:h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={aggregatedData}
              margin={{ top: 30, right: 30, left: 20, bottom: 40 }}
              barCategoryGap={8}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} opacity={0.3} />
              <XAxis
                dataKey="timeRange"
                tick={{ fill: CHART_COLORS.axis, fontSize: 12 }}
                tickMargin={10}
                axisLine={{ stroke: CHART_COLORS.tooltipBorder }}
              />
              <YAxis
                tickFormatter={(value) => `${value}%`}
                domain={[0, Math.ceil(maxPercentage * 1.1)]}
                tick={{ fill: CHART_COLORS.axis, fontSize: 12 }}
                axisLine={{ stroke: CHART_COLORS.tooltipBorder }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar
                dataKey="percentageOfTotal"
                fill={CHART_COLORS.primary}
                radius={[4, 4, 0, 0]}
                maxBarSize={60}
              >
                <LabelList dataKey="percentageOfTotal" content={renderCustomizedLabel} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default LeadTimeHistogramCard;
